import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Button, Spin } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import Card from '@src/containers/App/Auth/components/Card';
import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';

import styles from './styles.module.less';

const REQUEST_STATUS = {
  CONFIRM: 'confirm',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
  EXPIRED: 'expired',
} as const;

type RequestStatus = (typeof REQUEST_STATUS)[keyof typeof REQUEST_STATUS];

interface CancelTokenPayload {
  exp?: number;
}

const CancelMembershipChange = () => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const [status, setStatus] = useState<RequestStatus>(REQUEST_STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectUntilLabel, setRejectUntilLabel] = useState('');
  const hasRequestedRef = useRef(false);
  const inFlightRef = useRef(false);

  const token = useMemo(
    () => new URLSearchParams(location.search).get('token')?.trim() || '',
    [location.search],
  );

  const service = useMemo(() => new WorkJourneyManagementService(), []);

  const toDeadlineLabel = (deadlineMillis: number) =>
    intl.formatDate(deadlineMillis, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  const parseJwtPayload = (jwtToken: string): CancelTokenPayload | null => {
    try {
      const [, payloadBase64] = jwtToken.split('.');
      if (!payloadBase64) return null;
      const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map(char => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      );
      return JSON.parse(json) as CancelTokenPayload;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (hasRequestedRef.current) return;
    hasRequestedRef.current = true;

    if (!token) {
      setStatus(REQUEST_STATUS.ERROR);
      setErrorMessage(
        intl.formatMessage({ id: 'workJourney.membershipChange.cancel.missingToken' }),
      );
      return;
    }

    const payload = parseJwtPayload(token);
    if (payload?.exp && Number.isFinite(payload.exp)) {
      const deadlineMs = payload.exp * 1000;
      setRejectUntilLabel(toDeadlineLabel(deadlineMs));
      if (Date.now() > deadlineMs) {
        setStatus(REQUEST_STATUS.EXPIRED);
        return;
      }
    }

    setStatus(REQUEST_STATUS.CONFIRM);
  }, [intl, token]);

  const handleReject = useCallback(async () => {
    if (!token || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsSubmitting(true);
    try {
      const result = await service.rejectMembershipChangeByToken(token);
      if (result.ok) {
        setStatus(REQUEST_STATUS.SUCCESS);
        return;
      }
      if (result.status === 410 || result.data?.errorCode === 'expired') {
        setStatus(REQUEST_STATUS.EXPIRED);
        return;
      }
      setStatus(REQUEST_STATUS.ERROR);
      setErrorMessage(
        result.data?.error ||
          intl.formatMessage({ id: 'workJourney.membershipChange.cancel.errorGeneric' }),
      );
    } catch {
      setStatus(REQUEST_STATUS.ERROR);
      setErrorMessage(
        intl.formatMessage({ id: 'workJourney.membershipChange.cancel.errorGeneric' }),
      );
    } finally {
      setIsSubmitting(false);
      inFlightRef.current = false;
    }
  }, [intl, service, token]);

  return (
    <div className={styles.page}>
      <Card>
        {status === REQUEST_STATUS.LOADING && (
          <div className={styles.centered}>
            <Spin />
          </div>
        )}

        {status === REQUEST_STATUS.CONFIRM && (
          <>
            <h1 className={styles.title}>
              {intl.formatMessage({ id: 'workJourney.membershipChange.cancel.title' })}
            </h1>
            <p className={styles.body}>
              {intl.formatMessage({ id: 'workJourney.membershipChange.cancel.body' })}
            </p>
            {rejectUntilLabel ? (
              <p className={styles.deadline}>
                {intl.formatMessage(
                  { id: 'workJourney.membershipChange.cancel.deadline' },
                  { date: rejectUntilLabel },
                )}
              </p>
            ) : null}
            <Button type="primary" danger loading={isSubmitting} onClick={handleReject} block>
              {intl.formatMessage({ id: 'workJourney.membershipChange.cancel.confirm' })}
            </Button>
          </>
        )}

        {status === REQUEST_STATUS.SUCCESS && (
          <Alert
            type="success"
            showIcon
            message={intl.formatMessage({ id: 'workJourney.membershipChange.cancel.successTitle' })}
            description={intl.formatMessage({
              id: 'workJourney.membershipChange.cancel.successBody',
            })}
          />
        )}

        {status === REQUEST_STATUS.EXPIRED && (
          <Alert
            type="warning"
            showIcon
            message={intl.formatMessage({ id: 'workJourney.membershipChange.cancel.expiredTitle' })}
            description={intl.formatMessage({
              id: 'workJourney.membershipChange.cancel.expiredBody',
            })}
          />
        )}

        {status === REQUEST_STATUS.ERROR && (
          <Alert
            type="error"
            showIcon
            message={intl.formatMessage({ id: 'workJourney.membershipChange.cancel.errorTitle' })}
            description={errorMessage}
          />
        )}

        {status !== REQUEST_STATUS.LOADING && status !== REQUEST_STATUS.CONFIRM && (
          <Button className={styles.homeButton} onClick={() => history.push('/')} block>
            {intl.formatMessage({ id: 'workJourney.management.plan.success.backHome' })}
          </Button>
        )}
      </Card>
    </div>
  );
};

export default CancelMembershipChange;
